import { ReactNode } from 'react';

import { Button, Column, Modal, overlayEscape } from 'components/Blocks';
import { cn } from 'styles';
import { useToggleV2 } from 'hooks';
import { useLang } from 'locale';

interface ConfirmButtonProps {
  text: string;
  icon: ReactNode;
  title: string;
  description: string;
  confirmText?: string;
  action: () => Promise<unknown>;
}

const LARGE_DESCRIPTION_SIZE = 120;

export function ConfirmButton({
  confirmText,
  text,
  description,
  icon,
  title,
  action,
}: ConfirmButtonProps) {
  const { t } = useLang();

  const [loading, loadActions] = useToggleV2();

  const execute = async () => {
    loadActions.turnOn();

    await action();

    loadActions.turnOff();

    overlayEscape();
  };

  return (
    <>
      <Modal
        showCloseButton={!loading}
        trigger={
          <Button
            color="primary"
            className="border-border bg-background text-foreground hover:text-primary"
            variant="outlined"
            round
            size="large"
            loading={loading}
          >
            {text}
          </Button>
        }
      >
        <Column alignItems="center">
          {icon}

          <h2 className="text-center text-xl font-semibold text-foreground-title">{title}</h2>

          <p
            className={cn(
              'text-center',
              description.length > LARGE_DESCRIPTION_SIZE ? 'max-w-[460px]' : 'max-w-96',
            )}
          >
            {description}
          </p>

          <Button className="mt-2 w-full" onClick={execute} loading={loading}>
            {confirmText ?? t('confirm')}
          </Button>

          <Button
            className="w-full"
            color="gray"
            variant="outlined"
            onClick={() => overlayEscape()}
          >
            {t('cancel')}
          </Button>
        </Column>
      </Modal>
    </>
  );
}
